import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccess } from '@/access/useAccess'
import type { PostCategory } from '@/api/pending'
import { CATEGORY_LABEL } from '@/features/approvals/labels'
import { Button, Caution, Chip, Eyebrow, Field, Icon, Modal, useToast } from '@/ui'
import { useCreatePoll, useCreatePost, unitsQuery } from './api'
import { AudiencePicker } from './AudiencePicker'
import {
  defaultEndDate,
  EMPTY_AUDIENCE,
  hoursUntilEndOf,
  resolveAudience,
  toDateInput,
  type AudienceSelection,
} from './audience'
import styles from './PublishModal.module.css'

export type PublishKind = 'post' | 'poll'

const CATEGORIES: PostCategory[] = [
  'announcement',
  'warning',
  'danger_warning',
  'reminder',
  'doubt',
  'suggestion',
]

export const MIN_OPTIONS = 2
export const MAX_OPTIONS = 5

export function PublishModal({ kind, onClose }: { kind: PublishKind; onClose: () => void }) {
  const { condoId } = useAccess()
  const toast = useToast()
  const units = useQuery(unitsQuery(condoId)).data ?? []
  const createPost = useCreatePost(condoId)
  const createPoll = useCreatePoll(condoId)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState<PostCategory>('announcement')
  const [options, setOptions] = useState(['', ''])
  const [audience, setAudience] = useState<AudienceSelection>(EMPTY_AUDIENCE)
  const [endDate, setEndDate] = useState(() => defaultEndDate())
  const [failed, setFailed] = useState(false)

  const isPoll = kind === 'poll'
  const unitIds = resolveAudience(audience, units)
  const today = toDateInput(new Date())
  const endDateError = isPoll && endDate < today ? 'Escolha uma data a partir de hoje.' : undefined
  const valid =
    title.trim() !== '' &&
    body.trim() !== '' &&
    (unitIds === null || unitIds.length > 0) &&
    (!isPoll ||
      (options.filter((o) => o.trim()).length >= MIN_OPTIONS && endDate !== '' && !endDateError))
  const busy = createPost.isPending || createPoll.isPending

  function onSuccess() {
    toast(
      isPoll
        ? `Enquete “${title.trim()}” aberta para votação`
        : `“${title.trim()}” publicado no feed`,
    )
    onClose()
  }

  function submit() {
    setFailed(false)
    const callbacks = { onSuccess, onError: () => setFailed(true) }
    const audienceIds = unitIds === null ? {} : { unit_ids: unitIds }
    if (isPoll) {
      createPoll.mutate(
        {
          question: title.trim(),
          body: body.trim(),
          options: options.map((o) => o.trim()).filter(Boolean),
          expires_in_hours: hoursUntilEndOf(endDate),
          ...audienceIds,
        },
        callbacks,
      )
    } else {
      createPost.mutate(
        { title: title.trim(), body: body.trim(), category, ...audienceIds },
        callbacks,
      )
    }
  }

  return (
    <Modal
      width={600}
      title={isPoll ? 'Abrir uma enquete' : 'Publicar um comunicado'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button icon="send" disabled={!valid} loading={busy} onClick={submit}>
            {isPoll ? 'Abrir enquete' : 'Publicar agora'}
          </Button>
        </>
      }
    >
      <Field
        label="Título"
        value={title}
        onChange={setTitle}
        autoFocus
        placeholder={
          isPoll ? 'Vamos estender o horário da academia?' : 'Limpeza da caixa d’água na quinta'
        }
      />
      <Field
        label={isPoll ? 'Contexto para os moradores' : 'Mensagem'}
        value={body}
        onChange={setBody}
        multiline
        placeholder="Escreva como se fosse colar no mural da portaria."
      />

      {isPoll ? (
        <>
          <div className={styles.group} role="group" aria-label="Opções">
            <span className={styles.label}>Opções</span>
            {options.map((option, i) => (
              <div key={i} className={styles.optionRow}>
                <input
                  className={styles.option}
                  value={option}
                  aria-label={`Opção ${i + 1}`}
                  placeholder={`Opção ${i + 1}`}
                  onChange={(e) =>
                    setOptions((list) => list.map((x, j) => (j === i ? e.target.value : x)))
                  }
                />
                {options.length > MIN_OPTIONS && (
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={`Remover opção ${i + 1}`}
                    onClick={() => setOptions((list) => list.filter((_, j) => j !== i))}
                  >
                    <Icon name="close" size={18} />
                  </button>
                )}
              </div>
            ))}
            {options.length < MAX_OPTIONS && (
              <div>
                <Button
                  variant="ghost"
                  size="s"
                  icon="add"
                  onClick={() => setOptions((list) => [...list, ''])}
                >
                  Adicionar opção
                </Button>
              </div>
            )}
          </div>
          <Field
            label="Encerra em"
            type="date"
            value={endDate}
            onChange={setEndDate}
            error={endDateError}
            hint="A votação fica aberta até o fim deste dia."
          />
        </>
      ) : (
        <div className={styles.group}>
          <Eyebrow>Categoria</Eyebrow>
          <div className={styles.chips} role="group" aria-label="Categoria">
            {CATEGORIES.map((c) => (
              <Chip key={c} selected={c === category} onClick={() => setCategory(c)}>
                {CATEGORY_LABEL[c]}
              </Chip>
            ))}
          </div>
        </div>
      )}

      <AudiencePicker units={units} value={audience} onChange={setAudience} />

      <Caution>
        Publicações da administração pulam a fila de aprovação — vão direto para o feed como{' '}
        {isPoll ? 'enquete aberta' : 'comunicado'}.
      </Caution>

      {failed && (
        <span className={styles.error} role="alert">
          <Icon name="error" size={16} />
          Não foi possível publicar agora. Tente novamente.
        </span>
      )}
    </Modal>
  )
}
